import { Link, useNavigate } from "@tanstack/react-router"
import { useId } from "react"

import { useCaptchaToken } from "@repo/captcha/use-captcha-token"
import { useSignInWithPassword } from "@repo/supabase/hooks/use-sign-in-with-password"
import { Button } from "@repo/ui/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
  useWatch,
} from "@repo/ui/components/ui/form"
import { Input } from "@repo/ui/components/ui/input"
import { toast } from "@repo/ui/components/ui/sonner"
import { useIsMobile } from "@repo/ui/hooks/use-mobile"
import { sharedConfig } from "~/lib/config/shared"
import {
  type SignInWithPasswordData,
  signInWithPasswordSchema,
} from "~/modules/auth/auth.schemas"

type SignInFormProps = {
  next?: string
}

export function SignInForm({ next }: SignInFormProps) {
  const toastId = useId()
  const navigate = useNavigate()
  const { getCaptchaTokenAsync, resetCaptchaToken } = useCaptchaToken()
  const isMobile = useIsMobile()

  const signIn = useSignInWithPassword()

  const form = useForm<SignInWithPasswordData>({
    formSchema: signInWithPasswordSchema,
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const onSubmit = async (data: SignInWithPasswordData) => {
    toast.loading("Signing in...", {
      id: toastId,
    })

    signIn.mutate(
      {
        ...data,
        options: {
          captchaToken: await getCaptchaTokenAsync(),
        },
      },
      {
        onSuccess() {
          toast.dismiss(toastId)
          navigate({ to: next || "/dashboard" })
        },
        onError() {
          toast.error("Failed to sign in", {
            id: toastId,
          })
        },
        onSettled() {
          resetCaptchaToken()
        },
      },
    )
  }

  const email = useWatch({ control: form.control, name: "email" })

  return (
    <Form {...form} onSubmit={form.handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-6">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="joe@example.com"
                  autoFocus={!isMobile}
                  autoComplete="email"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>Password</FormLabel>
                <Link
                  to="/auth/forgot-password"
                  className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                  viewTransition
                  tabIndex={-1}
                  search={{ email }}
                >
                  Forgot your password?
                </Link>
              </div>
              <FormControl>
                <Input
                  type="password"
                  autoComplete="current-password"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {signIn.error && (
          <p className="rounded-md bg-destructive/50 px-2 py-1 text-destructive-foreground text-sm">
            {signIn.error.message}
          </p>
        )}
        <Button
          form={form.id}
          type="submit"
          className="w-full"
          isLoading={signIn.isPending}
          loadingText="Signing in..."
        >
          Sign in
        </Button>
      </div>
      {sharedConfig.flags.signUpsEnabled && (
        <div className="mt-4 text-center text-sm">
          Don&apos;t have an account?{" "}
          <Link
            to="/auth/sign-up"
            className="underline underline-offset-4"
            viewTransition
          >
            Sign up
          </Link>
        </div>
      )}
    </Form>
  )
}
